/**
 * IDP-983: Shared builder that maps a microapp catalog entity's annotations to
 * the microapp-hosting template formData used for redeploy / deploy-to-staging.
 *
 * Extracted from MicroappAboutCard so the admin-page Environments widget
 * (MicroappEnvironmentsCard) can trigger a staging deploy using the exact same
 * pre-fill logic (DRY). The live-secrets augmentation (an authenticated API
 * call) stays in MicroappAboutCard.handleRedeploy.
 */

import { Entity } from '@backstage/catalog-model';
import { STAGING_ENABLED } from './stagingFeatureFlag';

export const GCP_PROJECT_ID_RE = /^[a-z][a-z0-9-]{4,28}[a-z0-9]$/;

export type MicroappRedeployFormData = Record<string, unknown>;

/**
 * Builds template formData from a microapp entity's annotations.
 * Returns null when the entity has no source-location (not a hosted microapp).
 */
export function buildMicroappRedeployFormData(
  entity: Entity,
): MicroappRedeployFormData | null {
  const annotations = entity.metadata.annotations || {};

  const sourceLocation = annotations['backstage.io/source-location'];
  if (!sourceLocation) return null;

  const githubRepoUrl = sourceLocation.replace(/url:/, '');

  const visibility = annotations['microapp/visibility'] || 'R&D Teammates Only';

  // IDP-983 backward compatibility: preserve the app's staging setting on
  // redeploy. Apps registered before this feature have no annotation → false,
  // so a redeploy deploys to PRODUCTION (unchanged behavior), never staging.
  // When STAGING_ENABLED flag is false, always force staging off.
  const stagingEnabled =
    STAGING_ENABLED && annotations['microapp/stagingEnabled'] === 'true';

  const containerPortStr = annotations['microapp/port'];
  const containerPort = containerPortStr
    ? parseInt(containerPortStr, 10)
    : 3001;

  const hasGsmSecrets = annotations['microapp/hasGsmSecrets'] === 'true';

  const gsmSecretKeysStr = annotations['microapp/gsmSecretKeys'] || '';
  const gsmSecretKeys = gsmSecretKeysStr
    ? gsmSecretKeysStr
        .split(',')
        .map(key => key.trim())
        .filter(key => key.length > 0)
    : [];

  const hasByogSecrets = annotations['microapp/hasByogSecrets'] === 'true';
  const rawByogGcpProjectId = annotations['microapp/byogGcpProjectId'] || '';
  const byogGcpProjectId = rawByogGcpProjectId;
  const isValidByogProjectId =
    !rawByogGcpProjectId || GCP_PROJECT_ID_RE.test(rawByogGcpProjectId);
  const effectiveHasByogSecrets = hasByogSecrets && isValidByogProjectId;
  const byogSecretNames = annotations['microapp/byogSecretNames'] || '';

  const needsPersistentStorage =
    annotations['microapp/persistentStorage'] === 'true';
  const storageSizeStr = annotations['microapp/storageSize'] || '';
  const storageSize = storageSizeStr
    ? parseInt(storageSizeStr.replace('Gi', ''), 10)
    : 10;

  const allowedUsersStr = annotations['microapp/allowedUsers'] || '';
  const allowedEmails =
    visibility === 'Custom (Specific Users)' && allowedUsersStr
      ? allowedUsersStr
          .split(',')
          .map(e => e.trim().toLowerCase())
          .filter(e => e.length > 0)
      : [];

  const ownersRaw = annotations['microapp/owners'] || '';
  const createdBy = annotations['microapp/createdBy'] || '';
  const ownerEmailSet = new Set<string>();
  ownersRaw.split(',').forEach(e => {
    const t = e.trim().toLowerCase();
    if (t) ownerEmailSet.add(t);
  });
  if (createdBy.trim()) ownerEmailSet.add(createdBy.trim().toLowerCase());
  const ownerEmails = Array.from(ownerEmailSet);

  const enableSecrets = hasGsmSecrets || effectiveHasByogSecrets;
  const secretsMode = effectiveHasByogSecrets
    ? 'BYOG (My Own GCP Project)'
    : 'SkyStage Managed';

  return {
    githubRepoUrl: { value: githubRepoUrl },
    visibility,
    containerPort,
    // Preserve staging opt-in across redeploys (false for legacy apps).
    enableStagingEnvironment: stagingEnabled,
    needsPersistentStorage,
    ...(allowedEmails.length > 0 && { allowedEmails }),
    ...(ownerEmails.length > 0 && { ownerEmails }),
    ...(needsPersistentStorage && {
      storageSize,
    }),
    ...(enableSecrets && {
      enableSecrets: true,
      secretsMode,
      ...(hasGsmSecrets &&
        gsmSecretKeys.length > 0 && {
          // Dual-env format: key + empty staging/production values
          // Empty values = "keep existing" on redeploy
          secrets: gsmSecretKeys.map(key => ({
            key,
            stagingValue: '',
            productionValue: '',
          })),
        }),
      ...(effectiveHasByogSecrets && {
        byogGcpProjectId,
        byogSecretNames,
      }),
    }),
  };
}
